import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { callTool } from "./api";
import { fetchAudienceCrmOptions } from "./campaign-builder/campaignBuilderApi";
import { pickAttributionCatalog } from "./campaign-builder/attributionOptions";
import { normalizeHallId } from "./floor/mapHall";
import { EVENT_KEY, segmentsMatch } from "./constants";
import {
  accountSearchHitToBoardRow,
  boothIdsFromAccountSearch,
  type AccountSearchResult,
  type RatingBoardResult,
  type RatingBoardRow,
} from "./ratings";
import type { LoadState } from "./types";

export type MyRatingFilter = "all" | "high" | "med" | "unrated";

export interface CampaignFilters {
  query: string;
  favoritesOnly: boolean;
  ownerMe: boolean;
  myRating: MyRatingFilter;
  minAi: string;
  segment: string;
  /** One attribution label. Empty means All. */
  attribution: string;
}

export const DEFAULT_CAMPAIGN_FILTERS: CampaignFilters = {
  query: "",
  favoritesOnly: false,
  ownerMe: false,
  myRating: "all",
  minAi: "",
  segment: "",
  attribution: "",
};

export function filtersAreActive(filters: CampaignFilters): boolean {
  return Boolean(
    filters.query.trim() ||
      filters.favoritesOnly ||
      filters.ownerMe ||
      filters.myRating !== "all" ||
      filters.minAi ||
      filters.segment.trim() ||
      filters.attribution.trim(),
  );
}

/** Floor funnel / drawer: ignore Account search query and Walk (Floor is already work-plan). */
export function floorFiltersAreActive(filters: CampaignFilters): boolean {
  return Boolean(
    filters.favoritesOnly || filters.myRating !== "all" || filters.minAi || filters.segment.trim(),
  );
}

export function floorFilterCount(filters: CampaignFilters): number {
  let count = 0;
  if (filters.favoritesOnly) count += 1;
  if (filters.myRating !== "all") count += 1;
  if (filters.minAi) count += 1;
  if (filters.segment.trim()) count += 1;
  return count;
}

export type FloorFilterChip = { id: string; label: string };

export function floorFilterChips(filters: CampaignFilters): FloorFilterChip[] {
  const chips: FloorFilterChip[] = [];
  if (filters.favoritesOnly) chips.push({ id: "favorites", label: "Favorites" });
  if (filters.minAi === "5") chips.push({ id: "minAi", label: "AI 5" });
  else if (filters.minAi === "4") chips.push({ id: "minAi", label: "AI 4+" });
  else if (filters.minAi === "3") chips.push({ id: "minAi", label: "AI 3+" });
  else if (filters.minAi.trim()) chips.push({ id: "minAi", label: `AI ${filters.minAi}+` });
  if (filters.myRating === "high") chips.push({ id: "myRating", label: "High" });
  else if (filters.myRating === "med") chips.push({ id: "myRating", label: "Med" });
  else if (filters.myRating === "unrated") chips.push({ id: "myRating", label: "Unrated" });
  if (filters.segment.trim()) chips.push({ id: "segment", label: filters.segment.replaceAll("_", " ") });
  return chips;
}

/** Account / Contacts funnel: ignore search query, include Walk and Attribution. */
export function boardFiltersAreActive(filters: CampaignFilters): boolean {
  return floorFiltersAreActive(filters) || filters.ownerMe || Boolean(filters.attribution.trim());
}

export function boardFilterCount(filters: CampaignFilters): number {
  return floorFilterCount(filters) + (filters.ownerMe ? 1 : 0) + (filters.attribution.trim() ? 1 : 0);
}

/** Account / Contacts toolbar chips (funnel only — not the search box). */
export function boardFilterChips(filters: CampaignFilters): FloorFilterChip[] {
  const chips = [...floorFilterChips(filters)];
  if (filters.ownerMe) chips.push({ id: "walk", label: "Walk" });
  const attribution = filters.attribution.trim();
  if (attribution) chips.push({ id: "attribution", label: attribution });
  return chips;
}

export function clearFloorFilterChip(filters: CampaignFilters, id: string): CampaignFilters {
  switch (id) {
    case "favorites":
      return { ...filters, favoritesOnly: false };
    case "minAi":
      return { ...filters, minAi: "" };
    case "myRating":
      return { ...filters, myRating: "all" };
    case "segment":
      return { ...filters, segment: "" };
    default:
      return filters;
  }
}

export function clearBoardFilterChip(filters: CampaignFilters, id: string): CampaignFilters {
  if (id === "walk") return { ...filters, ownerMe: false };
  if (id === "attribution") return { ...filters, attribution: "" };
  return clearFloorFilterChip(filters, id);
}

export function clearBoardFilters(filters: CampaignFilters): CampaignFilters {
  return clearFloorFilters(filters);
}

export function clearFloorFilters(filters: CampaignFilters): CampaignFilters {
  return { ...DEFAULT_CAMPAIGN_FILTERS, query: filters.query };
}

/** Floor map paint uses only Floor chips — not Account search query and not Walk. */
export function floorHighlightFilters(filters: CampaignFilters): CampaignFilters {
  return {
    ...DEFAULT_CAMPAIGN_FILTERS,
    favoritesOnly: filters.favoritesOnly,
    myRating: filters.myRating,
    minAi: filters.minAi,
    segment: filters.segment,
  };
}

export function ratingBoardQueryArgs(filters: CampaignFilters): {
  query?: string;
  favorites_only: boolean;
  owner_me: boolean;
  user_view?: "high" | "med";
  min_ai_rating?: number;
  segment?: string;
  attribution?: string;
  unratedOnly: boolean;
} {
  return {
    query: filters.query.trim() || undefined,
    favorites_only: filters.favoritesOnly,
    owner_me: filters.ownerMe,
    user_view: filters.myRating === "high" || filters.myRating === "med" ? filters.myRating : undefined,
    min_ai_rating: filters.minAi ? Number(filters.minAi) : undefined,
    segment: filters.segment.trim() || undefined,
    attribution: filters.attribution.trim() || undefined,
    unratedOnly: filters.myRating === "unrated",
  };
}

/** Args GrokBot `account_search` accepts. Board filters go in `filters` and also top-level. */
export function accountSearchQueryArgs(
  filters: CampaignFilters,
  extras?: { viewer?: string; campaignKey?: string; workPlan?: boolean; scopeToEvent?: boolean },
): {
  name?: string;
  query?: string;
  segment?: string;
  filters?: Record<string, unknown>;
} {
  const board = ratingBoardQueryArgs(filters);
  const bag: Record<string, unknown> = {};
  if (extras?.viewer) bag.viewer = extras.viewer;
  if (extras?.scopeToEvent !== false && extras?.campaignKey) bag.campaign_key = extras.campaignKey;
  if (extras?.workPlan) {
    bag.work_plan = true;
    bag.owner_me = true;
    // Keep going — Work My Plan ANDs with segment / query / other chips (no early return).
  }
  if (board.query) {
    bag.name = board.query;
    bag.query = board.query;
  }
  if (board.favorites_only) bag.favorites_only = true;
  if (board.owner_me) bag.owner_me = true;
  if (board.user_view) bag.user_view = board.user_view;
  if (board.min_ai_rating) bag.min_ai_rating = board.min_ai_rating;
  if (board.segment) bag.segment = board.segment;
  if (board.attribution) bag.attribution = board.attribution;
  if (board.unratedOnly) bag.unrated_only = true;
  return {
    name: board.query,
    query: board.query,
    segment: board.segment,
    filters: Object.keys(bag).length ? bag : undefined,
  };
}

/**
 * Full CRM contact roster (like Account with scopeToEvent: false).
 * Omits event_key so rows are not limited to contacts stamped with one show;
 * campaign_key still drives booth listings and journey for the active campaign.
 * Includes do_not_contact rows so CRM roster/search and Account → People can still find opted-out contacts.
 */
export function contactSearchQueryArgs(
  filters: CampaignFilters,
  extras?: { viewer?: string; campaignKey?: string },
): {
  campaign_key?: string;
  viewer?: string;
  include_do_not_contact: boolean;
  favorites_only: boolean;
  owner_me: boolean;
  user_view?: "high" | "med";
  min_ai_rating?: number;
  segment?: string;
  attribution?: string;
  unrated_only: boolean;
} {
  const board = contactSearchAccountFilterArgs(filters);
  const campaignKey = extras?.campaignKey?.trim() || EVENT_KEY;
  return {
    campaign_key: campaignKey,
    ...(extras?.viewer ? { viewer: extras.viewer } : {}),
    include_do_not_contact: true,
    ...board,
  };
}

/** Header filters for contact_search. favorites_only is starred contacts ∪ contacts of starred accounts. */
export function contactSearchAccountFilterArgs(filters: CampaignFilters): {
  favorites_only: boolean;
  owner_me: boolean;
  user_view?: "high" | "med";
  min_ai_rating?: number;
  segment?: string;
  attribution?: string;
  unrated_only: boolean;
} {
  const args = ratingBoardQueryArgs(filters);
  return {
    favorites_only: args.favorites_only,
    owner_me: args.owner_me,
    user_view: args.user_view,
    min_ai_rating: args.min_ai_rating,
    segment: args.segment,
    attribution: args.attribution,
    unrated_only: args.unratedOnly,
  };
}

/** Case-insensitive union. Keeps the first spelling of each label. */
export function distinctAttributionLabels(rows: Array<{ attribution?: unknown }>): string[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (!Array.isArray(row.attribution)) continue;
    for (const value of row.attribution) {
      if (typeof value !== "string") continue;
      const label = value.trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function labelListFromTool(data: unknown): string[] {
  if (Array.isArray(data)) return data.map((value) => String(value));
  if (!data || typeof data !== "object") return [];
  const record = data as { labels?: unknown; attributions?: unknown };
  const raw = Array.isArray(record.attributions)
    ? record.attributions
    : Array.isArray(record.labels)
      ? record.labels
      : [];
  return raw.map((value) => String(value));
}

/**
 * Same contact attribution catalog as the Campaign audience builder.
 * The campaign checkbox list wins over any account-row sample.
 */
export async function loadAttributionLabelOptions(eventKey: string): Promise<string[]> {
  try {
    const data = await fetchAudienceCrmOptions(eventKey, true);
    const fromCampaign = pickAttributionCatalog(data.attributions);
    if (fromCampaign.length) return fromCampaign;
  } catch {
    // Campaign catalog route can be down; try the same tools it uses.
  }
  try {
    const fromFacets = pickAttributionCatalog(
      labelListFromTool(await callTool("crm_audience_facets", {})),
    );
    if (fromFacets.length) return fromFacets;
  } catch {
    // Optional on older upstreams.
  }
  try {
    const fromTool = pickAttributionCatalog(
      labelListFromTool(await callTool("attribution_labels", {})),
    );
    if (fromTool.length) return fromTool;
  } catch {
    // The catalog read is not on this upstream yet.
  }
  const contacts = await callTool<{ results?: Array<{ attribution?: unknown }> }>("contact_search", {
    campaign_key: eventKey,
    limit: 200,
  }).catch(() => ({ results: [] }));
  return distinctAttributionLabels(contacts.results ?? []);
}

export function rowMatchesSegment(row: RatingBoardRow, segment: string): boolean {
  return segmentsMatch(row.segment, segment);
}

export function applyRatingBoardClientFilter(
  rows: RatingBoardRow[],
  unratedOnly: boolean,
  segment?: string,
): RatingBoardRow[] {
  const segmentFilter = segment?.trim() || "";
  if (!unratedOnly && !segmentFilter) return rows;
  return rows.filter((row) => {
    if (unratedOnly && row.my_view != null) return false;
    if (segmentFilter && !rowMatchesSegment(row, segmentFilter)) return false;
    return true;
  });
}

export function rowMatchesHall(rowHall: string | null | undefined, hallId: string): boolean {
  if (!rowHall) return true;
  const row = normalizeHallId(rowHall) ?? rowHall.replace(/^hall\s+/i, "").trim().toUpperCase();
  const want = normalizeHallId(hallId) ?? hallId.trim().toUpperCase();
  return row === want;
}

export function useCampaignFilters(): [CampaignFilters, (next: CampaignFilters) => void] {
  const [filters, setFilters] = useState<CampaignFilters>(DEFAULT_CAMPAIGN_FILTERS);
  return [filters, setFilters];
}

type RosterSearchMode = "account" | "contacts";

/** Live roster total for the Account search placeholder, and the numbered event-contact total on Contacts. */
export function useRosterMatchTotal(
  mode: RosterSearchMode | null,
  eventKey: string,
  campaignKey: string,
  actor: string,
  filters: CampaignFilters,
  contactQuery: string,
): { total: number | null; pending: boolean } {
  const viewer = actor.trim().toLowerCase();
  const [debouncedAccountQuery, setDebouncedAccountQuery] = useState(filters.query);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAccountQuery(filters.query), 200);
    return () => window.clearTimeout(timer);
  }, [filters.query]);

  const accountFilters = useMemo(
    () => ({ ...filters, query: debouncedAccountQuery }),
    [filters, debouncedAccountQuery],
  );
  const accountArgs = useMemo(
    () => accountSearchQueryArgs(accountFilters, { viewer, campaignKey, scopeToEvent: false }),
    [accountFilters, viewer, campaignKey],
  );
  const [total, setTotal] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const requestGen = useRef(0);
  void contactQuery;

  useEffect(() => {
    if (!mode) {
      setTotal(null);
      setPending(false);
      return;
    }
    const gen = ++requestGen.current;
    setPending(true);
    void (async () => {
      try {
        if (mode === "account") {
          const data = await callTool<AccountSearchResult>("account_search", {
            name: accountArgs.name,
            query: accountArgs.query,
            segment: accountArgs.segment,
            filters: { ...accountArgs.filters, include_total: true },
            include_total: true,
            limit: 1,
          });
          if (gen !== requestGen.current) return;
          setTotal(typeof data.total === "number" ? data.total : null);
        } else {
          const data = await callTool<{ total?: number }>("event_contact_list", {
            campaign_key: campaignKey.trim() || EVENT_KEY,
            limit: 1,
          });
          if (gen !== requestGen.current) return;
          setTotal(typeof data.total === "number" ? data.total : null);
        }
      } catch {
        if (gen !== requestGen.current) return;
        setTotal(null);
      } finally {
        if (gen === requestGen.current) setPending(false);
      }
    })();
  }, [mode, accountArgs, campaignKey, eventKey]);

  return { total, pending };
}

/** Floor map highlights from Floor chips only. Does not AND Walk, Account query, or hall. */
export function useFloorFilterBoothIds(
  eventKey: string,
  actor: string,
  filters: CampaignFilters,
  campaignKey?: string,
): { boothIds: ReadonlySet<string> | undefined; pending: boolean } {
  const viewer = actor.trim().toLowerCase();
  const highlightFilters = useMemo(() => floorHighlightFilters(filters), [filters]);
  const active = floorFiltersAreActive(highlightFilters);
  const args = useMemo(
    () => accountSearchQueryArgs(highlightFilters, { viewer, campaignKey }),
    [highlightFilters, viewer, campaignKey],
  );
  const unratedOnly = ratingBoardQueryArgs(highlightFilters).unratedOnly;
  const [boothIds, setBoothIds] = useState<Set<string> | undefined>(undefined);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!active) {
      setBoothIds(undefined);
      setPending(false);
      return;
    }
    let cancelled = false;
    setPending(true);
    setBoothIds(undefined);
    void (async () => {
      const ids = new Set<string>();
      let cursor: string | undefined;
      try {
        for (let page = 0; page < 10; page += 1) {
          const data = await callTool<AccountSearchResult>("account_search", {
            event_key: eventKey,
            name: args.name,
            query: args.query,
            segment: args.segment,
            filters: args.filters,
            limit: 200,
            cursor,
          });
          if (cancelled) return;
          for (const hit of data.results ?? []) {
            if (unratedOnly && hit.my_view != null) continue;
            for (const id of boothIdsFromAccountSearch(hit)) ids.add(id);
          }
          if (!data.has_more || !data.next_cursor) break;
          cursor = data.next_cursor;
        }
        if (!cancelled) {
          setBoothIds(ids);
          setPending(false);
        }
      } catch {
        if (!cancelled) {
          setBoothIds(new Set());
          setPending(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, eventKey, args, unratedOnly]);

  return { boothIds: active ? boothIds : undefined, pending: active && (pending || boothIds === undefined) };
}

export function useRatingBoard(
  eventKey: string,
  actor: string,
  filters: CampaignFilters,
  pageSize = 200,
  campaignKey?: string,
  enabled = true,
  options?: { workPlan?: boolean; reloadKey?: number; scopeToEvent?: boolean },
) {
  const viewer = actor.trim().toLowerCase();
  const [board, setBoard] = useState<LoadState<RatingBoardResult>>({ status: "loading" });
  const [rows, setRows] = useState<RatingBoardRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const workPlan = Boolean(options?.workPlan);
  const scopeToEvent = options?.scopeToEvent !== false;
  const reloadKey = options?.reloadKey ?? 0;
  const args = useMemo(
    () => accountSearchQueryArgs(filters, { viewer, campaignKey, workPlan, scopeToEvent }),
    [filters, viewer, campaignKey, workPlan, scopeToEvent],
  );
  const requestGen = useRef(0);

  const load = useCallback(
    async (mode: "replace" | "append" = "replace") => {
      if (!enabled) return;
      const gen = mode === "replace" ? ++requestGen.current : requestGen.current;
      if (mode === "append") {
        if (!nextCursor || loadingMore) return;
        setLoadingMore(true);
      } else {
        setBoard({ status: "loading" });
        setRows([]);
        setNextCursor(null);
        setHasMore(false);
      }
      try {
        const mapRows = (data: AccountSearchResult) =>
          applyRatingBoardClientFilter(
            (data.results ?? []).map((row) => accountSearchHitToBoardRow(row, campaignKey)),
            ratingBoardQueryArgs(filters).unratedOnly,
            args.segment,
          ).filter((row) => !workPlan || row.my_owner);

        if (mode === "replace") {
          let cursor: string | undefined;
          let all: RatingBoardRow[] = [];
          let last: AccountSearchResult | undefined;
          for (let page = 0; page < 20; page += 1) {
            const data = await callTool<AccountSearchResult>("account_search", {
              ...(scopeToEvent ? { event_key: eventKey } : {}),
              name: args.name,
              query: args.query,
              segment: args.segment,
              filters: args.filters,
              limit: pageSize,
              cursor,
            });
            if (gen !== requestGen.current) return;
            last = data;
            all = all.concat(mapRows(data));
            setRows(all);
            setNextCursor(data.next_cursor ?? null);
            setHasMore(Boolean(data.has_more));
            setBoard(
              all.length
                ? {
                    status: "ready",
                    data: {
                      results: all,
                      campaign_key: campaignKey,
                      has_more: data.has_more,
                      next_cursor: data.next_cursor,
                    },
                  }
                : { status: "empty", message: "No accounts match this search." },
            );
            if (!data.has_more || !data.next_cursor) break;
            cursor = data.next_cursor;
            setLoadingMore(true);
          }
          if (gen !== requestGen.current) return;
          setLoadingMore(false);
          if (!last?.has_more) {
            setHasMore(false);
            setNextCursor(null);
          }
          return;
        }

        const data = await callTool<AccountSearchResult>("account_search", {
          ...(scopeToEvent ? { event_key: eventKey } : {}),
          name: args.name,
          query: args.query,
          segment: args.segment,
          filters: args.filters,
          limit: pageSize,
          cursor: nextCursor ?? undefined,
        });
        if (gen !== requestGen.current) return;
        const incoming = mapRows(data);
        const mapped: RatingBoardResult = {
          results: incoming,
          campaign_key: campaignKey,
          has_more: data.has_more,
          next_cursor: data.next_cursor,
        };
        setRows((prev) => [...prev, ...incoming]);
        setNextCursor(data.next_cursor ?? null);
        setHasMore(Boolean(data.has_more));
        setBoard(
          incoming.length
            ? { status: "ready", data: mapped }
            : { status: "empty", message: "No accounts match this search." },
        );
      } catch (error) {
        if (gen !== requestGen.current) return;
        setBoard({
          status: "error",
          message: error instanceof Error ? error.message : "account_search failed",
        });
      } finally {
        if (mode === "append") setLoadingMore(false);
      }
    },
    [eventKey, viewer, args, campaignKey, nextCursor, loadingMore, pageSize, enabled, filters, scopeToEvent],
  );

  useEffect(() => {
    if (!enabled) {
      requestGen.current += 1;
      setBoard({ status: "idle" });
      setRows([]);
      setNextCursor(null);
      setHasMore(false);
      return;
    }
    void load("replace");
    // Filter/event identity only; load identity includes cursor for append.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventKey, viewer, args, campaignKey, pageSize, enabled, workPlan, reloadKey, scopeToEvent]);

  const loadMore = useCallback(() => {
    void load("append");
  }, [load]);

  const applyLocalTag = useCallback((accountId: string, patch: Record<string, unknown>) => {
    setRows((prev) => {
      const next = prev.map((row) => {
        if (row.account_id !== accountId) return row;
        const userView = patch.user_view;
        const segmentPatched = patch.segment === null || typeof patch.segment === "string";
        const segment = segmentPatched
          ? typeof patch.segment === "string"
            ? patch.segment.trim() || null
            : null
          : row.segment;
        const classification = segmentPatched
          ? { ...(row.classification ?? {}), segment_primary: segment }
          : row.classification;
        return {
          ...row,
          my_favorite: typeof patch.favorite === "boolean" ? patch.favorite : row.my_favorite,
          my_view:
            userView === "none"
              ? null
              : userView === "high" || userView === "med"
                ? userView
                : row.my_view,
          my_owner: typeof patch.owner === "boolean" ? patch.owner : row.my_owner,
          segment,
          segments: segmentPatched ? (segment ? [segment] : []) : row.segments,
          classification,
        };
      });
      const wanted = filters.segment.trim();
      if (!wanted) return next;
      return next.filter((row) => row.account_id !== accountId || rowMatchesSegment(row, wanted));
    });
  }, [filters.segment]);

  return { board, rows, load: () => load("replace"), loadMore, applyLocalTag, hasMore, loadingMore, viewer };
}
